import add from 'date-fns/add'
import { current, original } from 'immer'
import startOfDay from 'date-fns/startOfDay'

import { i18WithDefault as t } from '@shared/locale'
import {
  buildClosedDates,
  buildOpenDays,
  getThresholds,
  isWeightRequiredForSpecie,
  maximumSelectableDate,
  minimumSelectableDate,
} from '@shared/service_types'
import { Types } from 'service-api'
import { OrderClientDto } from 'service-api/src/types'
import { newCart, newStateData } from './booking_context'
import type {
  TBookingCart,
  TBookingContext,
  TBookingGroup,
  TBookingPet,
  TBreedsBySpecie,
  TCartGroupOffer,
  TCartGroupPetAddon,
  TCartVet,
  TOfferAvailability,
  TPetsBySpecie,
  TReducerActions,
} from './booking'
import { newPetGroup, newPetRecord } from './helper'
import { isEmpty } from '@shared/common'
import { toISODate } from 'components-ui'

const actions: TReducerActions = {
  LOGIN: (state: TBookingContext, _?: GenericSimpleBag) => {
    state.isValid = false
  },
  SET_BOOKING_CONFIG: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const mustUpdateDates = state.kind !== payload!.serviceTypeName
    const today = new Date()
    state.kind = payload!.serviceTypeName
    state.config.booking = payload!.value
    if (!state.startDate || mustUpdateDates) {
      state.startDate = minimumSelectableDate(today, state.config.booking)
      if (state.kind === 'daycare') {
        state.endDate = maximumSelectableDate(state.startDate, state.config.booking)
      } else {
        state.endDate = add(state.startDate, { days: 1 })
      }
    }
    state.closedDates = buildClosedDates(state.config.booking)
    state.openDays = buildOpenDays(state.config.booking)
  },
  RESET_STATE: (state: TBookingContext) => {
    resetState(state)
  },
  SET_LOCATION_CONFIG: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.config.location = payload!.value
  },
  SET_SERVICE_TYPE_CONFIG: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.config.serviceType = payload!.value
  },
  SET_SERVICE_TYPE: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.serviceType = payload!.value
  },
  SET_SPECIES_CONFIG: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const species: Types.LocationSpeciesClientDto[] = payload!.value
    state.config.species = species
    state.config.breedsBySpecie = species.reduce((acc, specie) => {
      acc[specie.id!] = specie.species?.breeds || []
      return acc
    }, {} as TBreedsBySpecie)
    const specieId = state.groups?.[0]?.specieId || species[0].id
    actions['SET_PET_GROUP_SPECIE'](state, { specieId, groupIdx: 0 })
  },
  SET_TEMPER_TEST_DATA: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.temperTest = payload!.value
  },
  SET_BOOKING_DATES: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.startDate = payload!.value[0]
    state.endDate = payload!.value[1]
    checkFormValid(state)
  },
  SET_PET_SEARCH_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    state.groups[val.groupIdx].pets[val.petIdx][val.fieldName as string] = val!.value as string
  },
  SET_PET_SEARCH_PROFILE: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    state.groups[val.groupIdx].pets[val.petIdx] = Object.assign({}, val!.value) as string
    validateForm(state, val.groupIdx)
  },
  CLEAR_PET_SEARCH_PROFILE: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    state.groups[val.groupIdx].pets[val.petIdx] = Object.assign({}, newPetRecord())
  },
  SET_PET_SEARCH_BREED_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    if (!val.breed || !val.breedId) {
      return
    }
    state.groups[val.groupIdx].pets[val.petIdx].breed = val.breed
    state.groups[val.groupIdx].pets[val.petIdx].breedId = val.breedId
    state.groups[val.groupIdx].pets[val.petIdx].breedInput = val.breed
  },
  CLEAR_PET_SEARCH_BREED_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    state.groups[val.groupIdx].pets[val.petIdx].breed = ''
    state.groups[val.groupIdx].pets[val.petIdx].breedId = ''
    state.groups[val.groupIdx].pets[val.petIdx].breedInput = ''
  },
  SET_BREED_INPUT_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    state.groups[val.groupIdx].pets[val.petIdx].breedInput = val.value
  },
  ADD_PET_SEARCH_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.groups[payload!.groupIdx].pets = state.groups[payload!.groupIdx].pets.concat(newPetRecord())
  },
  ADD_ROOM_SEARCH_FIELD: (state: TBookingContext, _payload?: GenericSimpleBag) => {
    state.groups.push(newPetGroup())
    state.isValid = false
    actions['SET_PET_GROUP_SPECIE'](state, { specieId: state.config.species![0].id, groupIdx: state.groups.length - 1 })
  },
  REMOVE_PET_SEARCH_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.groups[payload!.groupIdx].pets.splice(payload!.petIdx, 1)
    validateForm(state, payload!.groupIdx)
  },
  REMOVE_ROOM_SEARCH_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.groups.splice(payload!.roomIdx, 1)
  },
  ADD_CONFIRMATION_GROUPS: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    const receipt: Types.ReceiptClientDto = val!.receipt
    state.confirmation.groups = []

    receipt?.pets?.forEach((petInReceipt) => {
      const selectedPet = Object.assign({}, newPetRecord(), { name: petInReceipt?.displayName })
      const groupIdx = (receipt?.invoices || [])?.findIndex((invoice) => invoice?.pets?.includes(selectedPet.name))

      if (groupIdx === -1) {
        return
      }

      if (state.confirmation?.groups && !state.confirmation?.groups?.[groupIdx]) {
        state.confirmation.groups.push({ ...newPetGroup(), pets: [] })
      }
      state.confirmation?.groups?.[groupIdx]?.pets.push(Object.assign({}, selectedPet) as TBookingPet)
    })
  },
  SET_PET_GROUP_SPECIE: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const spc = state.config!.species!.find((s) => s.id === payload!.specieId)
    const totalWeight = getThresholds(state.config?.booking?.totalWeight, payload!.specieId)
    const petWeight = getThresholds(state.config?.booking?.weight, payload!.specieId)
    const petCount = getThresholds(state.config?.booking?.petCount, payload!.specieId)
    const group = state.groups[payload!.groupIdx]

    group.specieId = payload!.specieId
    group.specieName = spc?.name || ''
    group.maxWeight = isEmpty(totalWeight?.max) ? undefined : parseInt(String(totalWeight?.max), 0)
    group.maxPets = isEmpty(petCount?.max) ? undefined : parseInt(String(petCount?.max), 0)
    group.maxPetWeight = isEmpty(petWeight?.max) ? undefined : parseInt(String(petWeight?.max), 0)
    group.isWeightRequired = isWeightRequiredForSpecie(state, payload!.specieId)
    checkSpecies(state.groups)
  },
  CHECK_ROOM_WEIGHT: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const group = state.groups[payload!.groupIdx]
    checkPetWeight(group)
  },
  ADD_ERROR_MSG: (state: TBookingContext, payload?: GenericSimpleBag) => {
    // if the message already exists, do not load it again
    if (state.groups[payload!.group].errors.findIndex((e) => e.kind === payload!.error.kind) < 0) {
      state.groups[payload!.group].errors.push(payload!.error)
    }
  },
  BLUR_FIELD_VALIDATION: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const val = payload!
    const group = state.groups[val.groupIdx]
    const pet = group.pets[val.petIdx]
    isFieldEmpty(group, pet, val.fieldName)
    isWeightValid(pet)
  },
  SET_DONE_FORM_VALIDATION: (state: TBookingContext, _payload?: GenericSimpleBag) => {
    validateForm(state)
    if (!formHasErrors(state)) {
      state.searchBarOpen = false
    }
  },
  TOGGLE_SEARCH_BAR: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.searchBarOpen = !state.searchBarOpen
    if (payload?.action === 'close') {
      validateForm(state)
    }
  },
  SET_SEARCH_STATE: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.currentState = payload!.value
    state.data.formHandler = payload!.formHandler
  },

  SELECT_OFFER: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const { offer, petGroup, availability } = payload!
    const data = { offer, availability, qty: [1] }

    if (!state.cart.groups[petGroup]) {
      state.cart.groups[petGroup] = { offerSelected: data }
    } else {
      state.cart.groups[petGroup].offerSelected = data
    }
    calculateCartPrice(state.kind, state)
  },
  LOAD_OFFERS_ADDONS: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.offersAddOns = payload!.value
    if (payload!.searchState) {
      state.currentState = payload!.searchState
    }
  },
  LOAD_PET_ADDONS: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.petsAddOns = payload!.value
    if (payload!.searchState) {
      state.currentState = payload!.searchState
    }
  },
  TOGGLE_ROOM_ADDON: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const groupIdx = payload!.groupIdx
    const offer: Types.BookingSearchClientDto = payload!.value
    state.cart.groups[groupIdx].roomsAddOn = state.cart.groups[groupIdx]?.roomsAddOn || []

    const idx = state.cart.groups[groupIdx].roomsAddOn!.findIndex((o) => o.offer!.id === offer.offer!.id)

    if (idx < 0) {
      state.cart.groups[groupIdx].roomsAddOn!.push({
        offer: offer.offer!,
        availability: offer.availabilityGroups![groupIdx],
      })
    } else {
      state.cart.groups[groupIdx].roomsAddOn!.splice(idx, 1)
    }
    calculateCartPrice(state.kind, state)
  },
  TOGGLE_PET_ADDON: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const groupIdx = payload!.groupIdx
    const offer: Types.BookingSearchClientDto = payload!.value
    state.cart.groups[groupIdx].petsAddOn = state.cart.groups[groupIdx].petsAddOn || []

    const idx = state.cart.groups[groupIdx].petsAddOn!.findIndex((o) => o.offer!.id === offer.offer!.id)

    if (idx < 0) {
      state.cart.groups[groupIdx].petsAddOn!.push({
        offer: offer.offer!,
        availability: offer.availabilityGroups![groupIdx],
        pets: state.groups[groupIdx].pets.map((pet, petIdx) => ({
          id: pet.id,
          qty: offer.availabilityGroups?.[groupIdx].availabilities?.[petIdx].available ? 1 : 0,
        })),
      })
    } else {
      state.cart.groups[groupIdx].petsAddOn!.splice(idx, 1)
    }
    calculateCartPrice(state.kind, state)
  },
  INCREASE_PET_ADDON: (state: TBookingContext, payload?: GenericSimpleBag) => {
    updatePetAddonQty(1, state, payload)
    calculateCartPrice(state.kind, state)
  },
  DECREASE_PET_ADDON: (state: TBookingContext, payload?: GenericSimpleBag) => {
    updatePetAddonQty(-1, state, payload)
    calculateCartPrice(state.kind, state)
  },
  REMOVE_PET_ADDON: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const { groupIdx, petAddOnId, petId } = payload!
    const petsAddOn = state.cart.groups[groupIdx].petsAddOn!
    const petAddOnIdx = petsAddOn.findIndex((p) => p.offer.id === petAddOnId)
    const petIdx = petsAddOn[petAddOnIdx].pets.findIndex((p) => p.id === petId)

    // if there is only one pet in the addon, remove the whole addon
    // otherwise, remove only the pet
    if (petsAddOn[petAddOnIdx].pets.length === 1) {
      petsAddOn.splice(petAddOnIdx, 1)
    } else {
      petsAddOn[petAddOnIdx].pets.splice(petIdx, 1)
    }
  },
  LOAD_OFFER_AVAILABILITY: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const availabilities: Types.AvailabilityGroupClientDto[][] = payload!.value
    const start = state.startDate!
    state.data.offerAvailability = availabilities.reduce((acc, avail, idx) => {
      const offerDate = startOfDay(add(start, { days: idx }))
      acc.push({
        id: offerDate.getTime(),
        date: offerDate,
        availability: avail[0]!,
      })

      return acc
    }, [] as TOfferAvailability[])
  },
  SET_DAYCARE_DATES: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const selectedDate: Date = payload!.value
    if (selectedDate === null) {
      state.cart.groups[0].daycareSelected = []
      calculateCartPrice(state.kind, state)
      return
    }

    const availability = state.data.offerAvailability!.find((a) => a.id === selectedDate.getTime())!
    if (!availability) {
      return
    }

    if (!state.cart.groups[0].daycareSelected) {
      state.cart.groups[0].daycareSelected = [availability]
      calculateCartPrice(state.kind, state)
      return
    }

    const idx = state.cart.groups[0].daycareSelected.findIndex((dc) => dc.id === availability.id)
    if (idx < 0) {
      state.cart.groups[0].daycareSelected.push(availability)
    } else {
      state.cart.groups[0].daycareSelected.splice(idx, 1)
    }
    calculateCartPrice(state.kind, state)
  },
  SET_TEMPER_TEST_DATE: (state: TBookingContext, payload?: GenericSimpleBag) => {
    if (!state.cart.temperTest) {
      state.cart.temperTest = { date: undefined, time: undefined, price: undefined }
    }

    state.cart.temperTest!.date = payload!.value
    state.cart.temperTest!.time = undefined
    state.cart.temperTest!.timez = undefined
    state.cart.temperTest!.price = payload!.price
  },
  SET_TEMPER_TEST_TIME: (state: TBookingContext, payload?: GenericSimpleBag) => {
    if (!state.cart.temperTest) {
      state.cart.temperTest = { date: undefined, time: undefined, timez: undefined }
    }

    state.cart.temperTest!.time = payload!.value
    state.cart.temperTest!.timez = payload!.timez
  },
  SET_TEMPER_TEST_REQUIREMENT: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const groupIdx = payload!.groupIdx
    const petIdx = payload!.petIdx
    state.groups[groupIdx]!.pets![petIdx].temperTested = payload!.value
  },
  SET_TEMPER_TEST_OFFER: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.temperTestOffer = payload!.value
  },
  SET_PAYMENT_CONTACT: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const c = payload!.value

    state.cart.contact = {
      firstName: c.firstName,
      lastName: c.lastName,
      phone: c.phone,
      email: c.email,
      useMailingAddress: true,
      mailingAddress: {
        streetLine1: c.streetLine1,
        streetLine2: c.streetLine2,
        city: c.city,
        state: c.state,
        zipCode: c.zipCode,
        country: c.country,
      },
      billingAddress: {
        streetLine1: '',
        streetLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
    }
  },
  SET_PAYMENT_BILLING_ADDRESS: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const c = payload!.value
    if (state.cart.contact) {
      state.cart.contact.billingAddress = {
        streetLine1: c.streetLine1,
        streetLine2: c.streetLine2,
        city: c.city,
        state: c.state,
        zipCode: c.zipCode,
        country: c.country,
      }
    }
  },
  SET_RESERVATION_DATA: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.reservation = payload!.value
  },
  SET_PAYMENT_METHOD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.cart.payment.method = payload!.value
  },
  SET_CONTACT_FIELD: (state: TBookingContext, payload?: GenericSimpleBag) => {
    if (payload!.value.fieldName) {
      const { nspace, fieldName, value } = payload!.value || {}
      if (nspace) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state.cart.contact[nspace][fieldName] = value
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state.cart.contact[fieldName] = value
      }
    }
  },
  SET_CHECK_IN_OUT_FORM: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const field = payload!.value.fieldName as keyof TBookingCart
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    state.cart[field] = payload!.value.value as string
  },
  SET_VET_FORM: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const field = payload!.value.fieldName as keyof TCartVet
    state.cart.vet[field] = payload!.value.value as string
  },
  SET_ASYNC_ACTION: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.asyncAction = payload!.value
  },
  SET_CART_TOTAL: (state: TBookingContext, payload?: GenericSimpleBag) => {
    const order: OrderClientDto = payload!.value
    state.cart.total = (order.invoices || []).reduce(
      (total, invoice) =>
        total +
        (invoice.items || []).reduce((sum, { price = 0, qty = 0, tax = 0 }) => sum + (price * qty + (tax || 0)), 0),
      0
    )
  },
  SET_PET_PROFILES: (state: TBookingContext, payload?: GenericSimpleBag) => {
    state.data.petProfiles = petsBySpecie(state, payload!.value as Types.PetProfileClientDto[])
  },
}

/* eslint-disable no-console */
export const bookingReducer = (state: TBookingContext, action: TReducerAction) => {
  const fx = actions[action.type]
  fx?.(state, action.payload)

  /* istanbul ignore next */
  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed(action.type)
    if (!fx) {
      console.log('%c action not found ', 'background: #FF0000; color: #fff')
    }
    console.log('start', original(state))
    console.log('payload', action.payload)
    console.log('after', current(state))
    console.groupEnd()
  }
}
/* eslint-enable no-console */

const validateForm = (state: TBookingContext, groupIdx?: number) => {
  checkSpecies(state.groups)

  if (typeof groupIdx !== 'undefined') {
    const group = state.groups[groupIdx]
    checkPetWeight(group)
  }
  state.groups.forEach(checkGroupForm)
  checkFormValid(state)
}

const formHasErrors = (state: TBookingContext) => {
  return (
    state.groups.filter(
      (g) =>
        g.errors.length > 0 ||
        g.pets.filter((p) => Boolean(p.nameErrorMsg) || Boolean(p.breedErrorMsg) || Boolean(p.weightErrorMsg)).length >
          0
    ).length > 0
  )
}

const checkFormValid = (state: TBookingContext) => {
  let stCheck =
    state.groups?.[0]?.pets?.[0]?.name !== '' &&
    !formHasErrors(state) &&
    !isEmpty(state.startDate) &&
    !isEmpty(state.endDate)

  if (stCheck && state.kind === 'boarding') {
    // check that boarding has different start and end dates
    stCheck = toISODate(state.startDate) !== toISODate(state.endDate)
  }

  state.isValid = stCheck
}
const checkSpecies = (groups: TBookingGroup[]) => {
  groups.forEach((group) => {
    if (group.specieId != '') {
      const idx = group.errors.findIndex((g) => g.kind === 'unselectedSpecie')
      if (idx >= 0) {
        group.errors = group.errors.slice(0, idx).concat(group.errors.slice(idx + 1))
      }
    }
  })
}

const isWeightValid = (pet: TBookingPet) => {
  const weight = parseInt(pet.weight)
  if (weight <= 0) {
    pet.weightErrorMsg = t('searchBar.errors.minWeight')
  }
}

const checkPetWeight = (group: TBookingGroup) => {
  if (isEmpty(group.maxWeight) && isEmpty(group.maxPetWeight)) {
    return
  }

  const totalWeightInRoom = group.pets.reduce((acc, pet) => acc + (parseInt(pet.weight) || 0), 0)
  const petWeightInRoom = isEmpty(group.maxPetWeight)
    ? null
    : group.pets.some((pet) => parseInt(pet.weight) > (group.maxPetWeight ?? 0))

  group.errors = group.errors.filter((e) => !e.kind.startsWith('petWeight'))

  if (!isEmpty(group.maxWeight) && totalWeightInRoom > group.maxWeight!) {
    group.errors.push({
      kind: group.pets.length > 1 ? 'petWeightGroup' : 'petWeightPet',
      message: group.pets.length > 1 ? t('searchBar.errors.petWeightGroup') : t('searchBar.errors.petWeightPet'),
    })
  }

  if (petWeightInRoom) {
    group.errors.push({
      kind: 'petWeightMax',
      message: t('searchBar.errors.petWeightMax'),
    })
  }
}

const checkGroupForm = (group: TBookingGroup) => {
  const fields = ['name', 'breed']
  if (group.isWeightRequired) {
    fields.push('weight')
  }
  group.pets.forEach((p) => {
    fields.forEach((field) => isFieldEmpty(group, p, field))
    isWeightValid(p)
  })
}

const isFieldEmpty = (group: TBookingGroup, pet: TBookingPet, fieldName: string) => {
  /* eslint-disable @typescript-eslint/ban-ts-comment */
  // @ts-ignore
  if (pet[fieldName].trim() === '') {
    // @ts-ignore
    pet[`${fieldName}ErrorMsg`] = t('searchBar.errors.required')
  } else {
    // @ts-ignore
    pet[`${fieldName}ErrorMsg`] = undefined
  }

  if (fieldName === 'weight' && !isEmpty(group.maxPetWeight)) {
    const weight = parseInt(pet.weight)
    if (weight > group.maxPetWeight!) {
      pet.weightErrorMsg = t('searchBar.errors.maxWeight')
    }
  }

  /* eslint-enable @typescript-eslint/ban-ts-comment */
}

const calculateCartPrice = (kind: 'boarding' | 'daycare', state: TBookingContext) => {
  // const totalPets = state.groups.reduce((acc, g) => acc + g.pets.length, 0)
  const calcGroup = (data?: TCartGroupOffer[]) => {
    if (!data) {
      return 0
    }
    return data.reduce((acc, g) => acc + (g?.availability?.availabilities?.[0]?.price?.value || 0), 0)
  }
  const calcGroupAddOn = (data?: TCartGroupPetAddon[]) => {
    return (data || []).reduce(
      (acc, group) =>
        acc +
        group?.pets?.reduce(
          (tot, p, index) => tot + p.qty * (group?.availability?.availabilities?.[index]?.price?.value || 0),
          0
        ),
      0
    )
  }
  const calcDayCare = (data?: TOfferAvailability[]) => {
    if (!data) {
      return 0
    }
    return data.reduce((acc, g) => acc + (g?.availability?.availabilities?.[0]?.price?.value ?? 0), 0)
  }

  state.cart.total = state.cart.groups.reduce((acc, g) => {
    let offerValue = 0
    if (kind !== 'daycare') {
      offerValue = g.offerSelected?.availability?.availabilities?.[0]?.price?.value || 0
    }

    acc += offerValue + calcGroup(g.roomsAddOn) + calcGroupAddOn(g.petsAddOn) + calcDayCare(g.daycareSelected)
    return acc
  }, 0)
}

const updatePetAddonQty = (value: number, state: TBookingContext, payload?: GenericSimpleBag) => {
  const groupIdx = payload!.groupIdx
  const addOnId = payload!.addOnId
  const petId = payload!.petId

  const addonIdx = state.cart.groups[groupIdx].petsAddOn!.findIndex((o) => o.offer!.id === addOnId)
  if (addonIdx < 0) {
    return
  }
  const petIdx = state.cart.groups[groupIdx].petsAddOn![addonIdx].pets.findIndex((p) => p.id === petId)
  if (petIdx < 0) {
    return
  }

  if (value < 0 && state.cart.groups[groupIdx].petsAddOn![addonIdx].pets[petIdx].qty === 0) {
    return
  }

  state.cart.groups[groupIdx].petsAddOn![addonIdx].pets[petIdx].qty += value
}

const resetState = (state: TBookingContext) => {
  state.groups = [newPetGroup()] as TBookingGroup[]
  state.cart = newCart()
  state.data = {
    ...newStateData(),
    petProfiles: state.data.petProfiles,
    temperTest: state.data.temperTest,
  }
  state.startDate = undefined
  state.endDate = undefined
  state.isValid = false

  if (state.config.species) {
    actions['SET_PET_GROUP_SPECIE'](state, { specieId: state.config.species?.[0]?.id || '', groupIdx: 0 })
  }
}

const petsBySpecie = (state: TBookingContext, profiles: Types.PetProfileClientDto[]): TPetsBySpecie => {
  return profiles.reduce((acc, profile) => {
    const p: keyof TPetsBySpecie = speciePlatformToLocation(state, profile.breed?.speciesId || '')
    acc[p] = acc[p] || []
    acc[p].push({
      id: profile.pet!.id || '',
      name: profile.pet!.displayName || '',
      nameInput: '',
      nameErrorMsg: '',
      breed: profile.breed?.displayName || '',
      breedId: profile.breed?.id || '',
      breedInput: '',
      weight: isEmpty(profile.weight) ? '' : String(profile.weight),
      weightErrorMsg: '',
      temperTested: true,
    })
    return acc
  }, {} as TPetsBySpecie)
}

const speciePlatformToLocation = (state: TBookingContext, platformSpecieId: string) => {
  return state.config.species?.find((s) => s.species?.id === platformSpecieId)?.id || ''
}
